/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './viewing-schedule.stache';
import FeedService from 'scripts/services/base-feed-service';
import Swiper from 'swiper';
import dayjs from 'dayjs';
import _ from 'lodash';

const scheduleDatesUrl = window.globalSettings.feeds.schedule.scheduleDatesUrl;

Component.extend({
    tag: 'viewing-schedule',
    template,
    viewModel: {
        define: {
            scheduleDate: {
                type: 'string',
                value: null,
            },
            days: {
                type: 'array',
                value: [],
            },
            daysDelta: {
                type: 'number',
                value: 1,
            },
        },
        onTabChange(event) {
            const slideIndex = _.findIndex(this.days, [
                'key',
                event.payload.value,
            ]);
            this.attr('scheduleDate', event.payload.value);
            this.swiper.slideTo(slideIndex);
        },
    },
    events: {
        inserted() {
            this.getDates();
        },

        initSwiper() {
            const daysDelta = this.viewModel.attr('daysDelta');
            const initialSlide = this.viewModel.attr('days').length - daysDelta;
            const config = {
                initialSlide,
                autoHeight: true,
            };

            this.viewModel.swiper = new Swiper(
                '.c-viewing-schedule__swiper',
                config,
            );

            this.viewModel.swiper.on('transitionEnd', () => {
                this.viewModel.attr(
                    'scheduleDate',
                    this.viewModel.days[this.viewModel.swiper.activeIndex].key,
                );
            });

            this.updateSwiper();
        },

        updateSwiper: _.debounce(function() {
            if (this.viewModel.swiper.height > 100) {
                return;
            }

            this.viewModel.swiper.update();
            this.updateSwiper();
        }, 500),

        getDates() {
            new FeedService({
                feedUrl: scheduleDatesUrl,
                fromCache: false,
                autoUpdate: false,
            })
                .on('data', (type, data) => {
                    if (data && data.tags && data.tags.length) {
                        const days = data.tags.map((day) => {
                            return Object.assign({}, day, {
                                capitalized: day.title.toUpperCase(),
                            });
                        });

                        const scheduleDateKey = this.getScheduleDate(
                            data.tags,
                            data.defaultTagKey,
                        );
                        this.viewModel.attr('days', days);
                        this.viewModel.attr('scheduleDate', scheduleDateKey);
                        this.initSwiper();
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching schedule dates', error);
                })
                .fetch();
        },

        getDateStamp(dateEntry) {
            const partedDate = dateEntry.split('_');
            const yearEntry = partedDate[2];
            const monthEntry = partedDate[0];
            const dayEntry = partedDate[1];
            const stampEntry = yearEntry + monthEntry + dayEntry;
            return parseInt(stampEntry, 10);
        },

        getScheduleDate: function(dates, defaultDate) {
            const dateFormat = 'MM_DD_YYYY';
            const currentDateEntry = dayjs(defaultDate, dateFormat).format(
                dateFormat,
            );
            const currentDateStamp = this.getDateStamp(currentDateEntry);

            const dateCandidate = dates.find((date, index) => {
                if (date.key === currentDateEntry) {
                    const daysDelta = dates.length - index;
                    this.viewModel.attr('daysDelta', daysDelta);
                    return true;
                }

                const dateStamp = this.getDateStamp(date.key);
                if (dateStamp > currentDateStamp) {
                    const daysDelta = dates.length - index;
                    this.viewModel.attr('daysDelta', daysDelta);
                    return true;
                }

                return false;
            });

            if (dateCandidate) {
                return dateCandidate.key;
            } else if (dates.length) {
                const lastPassedDate = dates[dates.length - 1];
                return lastPassedDate.key;
            }

            return defaultDate;
        },
    },
});
